import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion, useInView, animate } from "framer-motion";
import {
  Inbox, MessageSquare, Paperclip, Users, Bell, EyeOff, Gauge,
  Building2, Wrench, Hotel, Factory, ArrowRight, CheckCircle, ShieldCheck,
  LayoutDashboard, Menu, X, Check, Clock, Mail, Server, Globe, Quote,
  SprayCan, Shield, HardHat, Leaf, Hammer, Briefcase,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.21, 0.47, 0.32, 0.98] } },
};
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const SECTOR_MARQUEE = [
  { icon: SprayCan, label: "Nettoyage & Entretien" },
  { icon: Shield, label: "Sécurité & Gardiennage" },
  { icon: HardHat, label: "Bâtiment & Travaux" },
  { icon: Wrench, label: "Plomberie & Chauffage" },
  { icon: Leaf, label: "Espaces Verts" },
  { icon: Hammer, label: "Maintenance Immobilière" },
  { icon: Building2, label: "Syndics & Copropriétés" },
  { icon: Hotel, label: "Hôtellerie & Restauration" },
  { icon: Briefcase, label: "Gestion de Bureaux" },
];
const TESTIMONIALS = [
  { quote: "Nous avons divisé par deux le temps passé à gérer les demandes clients.", author: "Responsable exploitation" },
  { quote: "Enfin une vision claire : on sait toujours où en est chaque demande, sans relancer.", author: "Direction qualité" },
];

const STATS = [
  { to: 1500, suffix: "+", label: "Demandes traitées", testid: "stat-demandes" },
  { to: 98, suffix: " %", label: "Clients satisfaits", testid: "stat-satisfaction" },
  { to: 50, suffix: "+", label: "Entreprises accompagnées", testid: "stat-secteurs" },
  { to: 100, suffix: " %", label: "Historique tracé", testid: "stat-historique" },
];

function StatCounter({ to, suffix = "", label, testid }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.21, 0.47, 0.32, 0.98],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <div ref={ref} className="text-center" data-testid={testid}>
      <div className="font-heading text-4xl font-extrabold tracking-tight text-primary sm:text-5xl lg:text-6xl">
        {Math.round(val).toLocaleString("fr-FR")}{suffix}
      </div>
      <div className="mt-2 text-sm font-semibold text-muted-foreground">{label}</div>
    </div>
  );
}

function StatsBand() {
  return (
    <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background" data-testid="stats-section">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
        <motion.div className="grid grid-cols-2 gap-8 sm:gap-10 lg:grid-cols-4"
          variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
          {STATS.map((s) => (
            <motion.div key={s.testid} variants={fadeUp}>
              <StatCounter to={s.to} suffix={s.suffix} label={s.label} testid={s.testid} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function SocialProofMarquee() {
  const chips = SECTOR_MARQUEE;
  return (
    <section className="overflow-hidden border-b border-border bg-card" data-testid="social-proof">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">
        <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">
          Conçu pour les métiers de services et bien plus encore
        </p>
        <div className="marquee-track relative mt-6 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]" tabIndex={0} data-testid="social-proof-marquee">
          <div className="flex w-max animate-marquee gap-4">
            {[...chips, ...chips].map((s, i) => (
              <div key={i} className="flex shrink-0 items-center gap-2.5 rounded-sm border border-border bg-background px-5 py-3">
                <s.icon className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.8} />
                <span className="whitespace-nowrap text-sm font-semibold">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <motion.div className="mt-10 grid gap-5 sm:grid-cols-2" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
          {TESTIMONIALS.map((t, i) => (
            <motion.blockquote key={i} variants={fadeUp} data-testid={`testimonial-${i}`}
              className="rounded-sm border border-border bg-background p-6 transition-shadow duration-300 ease-out hover:shadow-lg sm:p-8">
              <Quote className="h-7 w-7 text-primary/30" strokeWidth={1.8} />
              <p className="mt-3 font-heading text-lg font-semibold leading-relaxed sm:text-xl">« {t.quote} »</p>
              <footer className="mt-4 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{t.author}</footer>
            </motion.blockquote>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Building2, title: "Portail client personnalisé", text: "Vos clients créent et suivent leurs demandes depuis un espace dédié." },
  { icon: Inbox, title: "Tickets centralisés", text: "Toutes les demandes sont regroupées, filtrées et suivies au même endroit." },
  { icon: Users, title: "Assignation intelligente", text: "Chaque demande peut être dirigée vers le bon collaborateur selon sa catégorie." },
  { icon: MessageSquare, title: "Fil de discussion intégré", text: "Les échanges restent liés au ticket, avec un historique clair." },
  { icon: Paperclip, title: "Photos et documents", text: "Les clients joignent directement les preuves, photos ou documents nécessaires." },
  { icon: EyeOff, title: "Notes internes sécurisées", text: "Vos équipes peuvent échanger entre elles sans visibilité côté client." },
  { icon: Bell, title: "Notifications email", text: "Les emails alertent les utilisateurs, mais le traitement reste dans Suivio360." },
  { icon: Gauge, title: "Suivi qualité", text: "Statuts, délais, historique et traçabilité permettent de mieux piloter." },
];

const SECTORS = [
  { icon: Factory, label: "Sociétés de nettoyage" },
  { icon: Wrench, label: "Maintenance & services techniques" },
  { icon: Building2, label: "Syndics & copropriétés" },
  { icon: Hotel, label: "Hôtels & hébergement" },
  { icon: Factory, label: "Entreprises multisites" },
  { icon: Users, label: "Prestataires de services" },
];

const STEPS = [
  "La société crée ses clients entreprise.",
  "Le client crée une demande depuis son portail.",
  "Le ticket est assigné au bon collaborateur.",
  "Les échanges, fichiers et notes restent centralisés.",
  "Le ticket est clôturé avec un historique complet.",
];

const SECURITY = [
  "Séparation stricte des sociétés clientes",
  "Accès par rôles (admin, manager, collaborateur, client)",
  "Notes internes invisibles côté client",
  "Logs d'activité complets",
  "Historique complet de chaque ticket",
];

const PLANS = [
  { name: "Découverte", price: "Gratuit", features: ["2 utilisateurs", "1 client entreprise", "30 tickets / mois", "Idéal pour tester"], highlight: false },
  { name: "Croissance", price: "23 €", suffix: "/ utilisateur / mois", features: ["Clients entreprise illimités", "Jusqu'à 20 utilisateurs", "2 000 tickets / mois", "Adapté aux équipes structurées"], highlight: true },
  { name: "Sur Mesure", price: "Sur devis", features: ["Utilisateurs illimités", "Tickets illimités", "Accompagnement dédié", "Besoins avancés"], highlight: false },
];

const slug = (s) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/\s+/g, "-");

function DashboardMockup({ testid = "hero-mockup" }) {
  return (
    <div className="rounded-sm border border-border bg-card shadow-2xl" data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300 dark:bg-neutral-600" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300 dark:bg-neutral-600" />
        <span className="ml-3 text-xs font-semibold text-muted-foreground">Suivio360 — Tickets</span>
      </div>
      <div className="grid grid-cols-3 gap-3 p-4">
        {["Ouverts", "En cours", "Clôturés"].map((l, i) => (
          <div key={l} className="rounded-sm border border-border bg-background p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</div>
            <div className={`mt-1 font-heading text-xl font-semibold ${i === 1 ? "text-primary" : ""}`}>{[12, 7, 48][i]}</div>
          </div>
        ))}
      </div>
      <div className="space-y-2 px-4 pb-4">
        {[["TCK-0001", "Canalisation bouchée", "En cours", "amber"], ["TCK-0002", "Demande d'intervention supplémentaire", "Nouveau", "neutral"], ["TCK-0003", "Nettoyage non conforme", "Clôturé", "green"]].map(([n, s, st, c]) => (
          <div key={n} className="flex items-center gap-3 rounded-sm border border-border bg-background px-3 py-2.5">
            <span className="font-mono text-[10px] text-muted-foreground">{n}</span>
            <span className="min-w-0 flex-1 truncate text-xs font-medium">{s}</span>
            <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${c === "amber" ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200" : c === "green" ? "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300" : "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900"}`}>{st}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ label, tone }) {
  const styles = {
    amber: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
    green: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300",
    neutral: "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900",
  };
  return <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${styles[tone]}`}>{label}</span>;
}

function MockupFrame({ title, testid, children }) {
  return (
    <div className="overflow-hidden rounded-sm border border-border bg-card shadow-lg" data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className="h-2 w-2 rounded-full bg-red-500" />
        <span className="h-2 w-2 rounded-full bg-neutral-300 dark:bg-neutral-600" />
        <span className="h-2 w-2 rounded-full bg-neutral-300 dark:bg-neutral-600" />
        <span className="ml-3 truncate text-[11px] font-semibold text-muted-foreground">{title}</span>
      </div>
      <div className="p-3 sm:p-4">{children}</div>
    </div>
  );
}

function TicketsListMockup() {
  const rows = [
    ["TCK-0004", "Intervention à planifier", "Client Démo", "Nouveau", "neutral"],
    ["TCK-0002", "Demande d'intervention supplémentaire", "Client Exemple", "Nouveau", "neutral"],
    ["TCK-0001", "Demande de suivi", "Client Démo", "En cours", "amber"],
    ["TCK-0003", "Nettoyage non conforme", "Client Exemple", "Clôturé", "green"],
  ];
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1 rounded-sm border border-border bg-background px-3 py-1.5 text-[11px] text-muted-foreground">Rechercher un ticket…</div>
        {["Tous", "En cours", "Clôturés"].map((f, i) => (
          <span key={f} className={`rounded-sm border px-2 py-1.5 text-[10px] font-semibold ${i === 0 ? "border-primary bg-primary text-white" : "border-border text-muted-foreground"}`}>{f}</span>
        ))}
      </div>
      {rows.map(([n, s, cl, st, tone]) => (
        <div key={n} className="flex items-center gap-3 rounded-sm border border-border bg-background px-3 py-2.5">
          <span className="font-mono text-[10px] text-muted-foreground">{n}</span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">{s}</div>
            <div className="truncate text-[10px] text-muted-foreground">{cl}</div>
          </div>
          <StatusPill label={st} tone={tone} />
        </div>
      ))}
    </div>
  );
}

function TicketDetailMockup() {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="font-mono text-[10px] text-muted-foreground">TCK-0001</span>
          <span className="truncate text-xs font-semibold">Demande de suivi</span>
        </div>
        <StatusPill label="En cours" tone="amber" />
      </div>
      <div className="max-w-[85%] rounded-sm border border-border bg-background p-2.5">
        <div className="text-[10px] font-semibold text-muted-foreground">Client Démo</div>
        <p className="mt-1 text-[11px] leading-relaxed">Bonjour, pouvez-vous me confirmer la prise en compte de ma demande ?</p>
      </div>
      <div className="ml-auto max-w-[85%] rounded-sm border border-primary/30 bg-primary/5 p-2.5">
        <div className="text-[10px] font-semibold text-primary">Société Exemple</div>
        <p className="mt-1 text-[11px] leading-relaxed">Bonjour, votre demande est bien enregistrée et en cours de traitement.</p>
      </div>
      <div className="rounded-sm border border-dashed border-red-300 bg-red-50 p-2.5 dark:border-red-900 dark:bg-red-950/40">
        <div className="text-[10px] font-semibold text-red-700 dark:text-red-300">Note interne — non visible par le client</div>
        <p className="mt-1 text-[11px] leading-relaxed text-red-900/80 dark:text-red-200/80">Intervention à planifier cette semaine.</p>
      </div>
    </div>
  );
}

function PortalMockup() {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold">Portail — Société Exemple</span>
        <span className="rounded-sm bg-primary px-2.5 py-1.5 text-[10px] font-semibold text-white">+ Nouvelle demande</span>
      </div>
      {[["TCK-0001", "Canalisation bouchée", "En cours", "amber"], ["TCK-0002", "Demande d'intervention supplémentaire", "Nouveau", "neutral"]].map(([n, s, st, tone]) => (
        <div key={n} className="flex items-center gap-3 rounded-sm border border-border bg-background px-3 py-2.5">
          <span className="font-mono text-[10px] text-muted-foreground">{n}</span>
          <span className="min-w-0 flex-1 truncate text-xs font-medium">{s}</span>
          <StatusPill label={st} tone={tone} />
        </div>
      ))}
      <p className="pt-1 text-center text-[10px] text-muted-foreground">Vos demandes suivies en toute transparence</p>
    </div>
  );
}

function EmailLogMockup() {
  const rows = [
    ["Ticket créé — TCK-0001", "Notification envoyée au client"],
    ["Réponse à votre demande — TCK-0002", "Notification envoyée au client"],
    ["Ticket clôturé — TCK-0003", "Notification envoyée au client"],
  ];
  return (
    <div className="space-y-2">
      {rows.map(([t, s]) => (
        <div key={t} className="flex items-center gap-3 rounded-sm border border-border bg-background px-3 py-2.5">
          <Mail className="h-3.5 w-3.5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">{t}</div>
            <div className="truncate text-[10px] text-muted-foreground">{s}</div>
          </div>
          <StatusPill label="Envoyé" tone="green" />
        </div>
      ))}
    </div>
  );
}

function DemoForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", users_estimate: "", sector: "", website: "" });
  const [sent, setSent] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/demo-requests", {
        name: form.name, company: form.company, email: form.email,
        phone: form.phone || null, message: form.message || null,
        users_estimate: form.users_estimate || null, sector: form.sector || null,
        website: form.website || null,
      });
      setSent(true);
      toast.success("Demande envoyée — nous revenons vers vous rapidement.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-sm border border-green-300 bg-green-50 p-8 text-center dark:border-green-800 dark:bg-green-950" data-testid="demo-form-success">
        <CheckCircle className="mx-auto h-10 w-10 text-green-600" />
        <h3 className="mt-4 text-xl font-semibold">Demande bien reçue</h3>
        <p className="mt-2 text-sm text-muted-foreground">Merci {form.name} — l'équipe Suivio360 vous recontacte très vite pour organiser votre démo.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="demo-form">
      <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
        className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="demo-honeypot" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Nom *</Label><Input data-testid="demo-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></div>
        <div className="space-y-2"><Label>Société *</Label><Input data-testid="demo-company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-sm" /></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Email *</Label><Input data-testid="demo-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" /></div>
        <div className="space-y-2"><Label>Téléphone</Label><Input data-testid="demo-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" /></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Nombre approximatif d'utilisateurs</Label>
          <Select value={form.users_estimate} onValueChange={(v) => setForm({ ...form, users_estimate: v })}>
            <SelectTrigger data-testid="demo-users" className="rounded-sm"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {["1 à 5", "6 à 20", "21 à 50", "Plus de 50"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Secteur d'activité</Label>
          <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
            <SelectTrigger data-testid="demo-sector" className="rounded-sm"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {["Nettoyage / propreté", "Maintenance / services techniques", "Syndic / copropriété", "Hôtellerie", "Autre"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2"><Label>Message</Label><Textarea data-testid="demo-message" rows={compact ? 2 : 4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm" placeholder="Parlez-nous de vos besoins…" /></div>
      <Button data-testid="demo-submit" type="submit" disabled={saving} className="w-full rounded-sm bg-primary text-white hover:bg-red-700 sm:w-auto">
        {saving ? "Envoi…" : "Demander une démo"}
      </Button>
    </form>
  );
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div className="min-h-dvh bg-background" data-testid="landing-page">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary font-heading font-bold text-white">S</div>
            <span className="font-heading text-lg font-semibold">Suivio<span className="text-primary">360</span></span>
          </div>
          <nav className="hidden items-center gap-6 text-sm md:flex">
            <button data-testid="nav-features" onClick={() => scrollTo("fonctionnalites")} className="text-muted-foreground transition-colors hover:text-foreground">Fonctionnalités</button>
            <button data-testid="nav-pour-qui" onClick={() => scrollTo("pour-qui")} className="text-muted-foreground transition-colors hover:text-foreground">Pour qui</button>
            <button data-testid="nav-offres" onClick={() => scrollTo("offres")} className="text-muted-foreground transition-colors hover:text-foreground">Offres</button>
            <button data-testid="nav-demo" onClick={() => scrollTo("demo")} className="text-muted-foreground transition-colors hover:text-foreground">Démo</button>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-button"
              className="rounded-sm border border-border px-4 py-2 text-sm font-semibold transition-colors hover:bg-accent">
              Se connecter
            </Link>
            <button data-testid="landing-demo-button" onClick={() => scrollTo("demo")}
              className="hidden rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 sm:block">
              Demander une démo
            </button>
            <button className="rounded-sm border border-border p-2 md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="landing-menu-button">
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="border-t border-border bg-background px-4 py-3 md:hidden" data-testid="landing-mobile-menu">
            {[["fonctionnalites", "Fonctionnalités"], ["pour-qui", "Pour qui"], ["offres", "Offres"], ["demo", "Démo"]].map(([id, label]) => (
              <button key={id} data-testid={`mobile-nav-${id}`} onClick={() => scrollTo(id)} className="block w-full py-2 text-left text-sm">{label}</button>
            ))}
          </div>
        )}
      </header>

      <section className="relative overflow-hidden border-b border-border">
        <div aria-hidden className="hero-gradient-anim pointer-events-none absolute inset-0 z-0 bg-gradient-to-tr from-primary/10 via-background to-primary/5 bg-[length:200%_200%]" style={{ animation: "heroGradientMove 15s ease infinite" }} />
        <div aria-hidden className="hero-blob-anim pointer-events-none absolute -right-32 -top-32 z-0 h-80 w-80 rounded-full bg-primary/15 blur-3xl" style={{ animation: "heroBlob 18s ease-in-out infinite" }} />
        <div aria-hidden className="hero-blob-anim pointer-events-none absolute -bottom-40 -left-24 z-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" style={{ animation: "heroBlob 22s ease-in-out infinite reverse" }} />
        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:py-24 lg:grid-cols-2">
          <motion.div className="min-w-0" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mb-4 inline-flex items-center gap-2 rounded-sm border border-border px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              <LayoutDashboard className="h-3.5 w-3.5 text-primary" /> SaaS de gestion des demandes clients
            </div>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
              Centralisez, suivez et traitez <span className="text-primary">toutes vos demandes clients.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              Suivio360 aide les prestataires de services à ne plus perdre aucune demande client : tickets, réclamations, photos, échanges et suivi sont centralisés dans une seule interface.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="hero-demo-button" onClick={() => scrollTo("demo")}
                className="inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700">
                Demander une démo <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/login" data-testid="hero-login-button"
                className="inline-flex items-center gap-2 rounded-sm border border-border px-6 py-3 text-sm font-semibold transition-colors hover:bg-accent">
                Accéder à l'espace équipe
              </Link>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2" data-testid="hero-benefits">
              {["Portail client dédié", "Tickets assignés automatiquement", "Historique complet des échanges"].map((b) => (
                <div key={b} className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary" /> {b}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div className="min-w-0" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <DashboardMockup />
          </motion.div>
        </div>
      </section>

      <StatsBand />

      <SocialProofMarquee />

      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Le problème</div>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Ne laissez plus vos demandes clients se perdre.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Emails, appels, SMS, WhatsApp, messages vocaux… quand les demandes arrivent de partout, il devient difficile de savoir qui traite quoi, ce qui est urgent et ce qui a réellement été clôturé.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              ["Demandes dispersées", "Entre la boîte mail, le téléphone et les messages, les demandes se perdent ou restent sans réponse."],
              ["Manque de visibilité", "Impossible de prouver quand une demande a été reçue, traitée ou clôturée."],
              ["Clients relancés inutilement", "Le client ne sait jamais où en est sa demande et doit relancer en permanence."],
            ].map(([t, d], i) => (
              <motion.div key={t} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-sm border border-border bg-background p-6 transition-transform transition-shadow duration-300 ease-out hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-3 h-1 w-8 bg-primary" />
                <h3 className="font-semibold">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="fonctionnalites" className="border-b border-border">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">La solution</div>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Une plateforme unique pour suivre chaque demande.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Avec Suivio360, chaque client dispose d'un espace dédié pour créer ses demandes. Chaque ticket arrive dans votre tableau de bord, classé, horodaté et prêt à être traité par la bonne personne.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="group rounded-sm border border-border bg-card p-5 transition-transform transition-shadow duration-300 ease-out hover:-translate-y-1.5 hover:border-primary/40 hover:shadow-xl" data-testid={`feature-${i}`}>
                <f.icon className="h-5 w-5 text-primary transition-transform duration-300 ease-out group-hover:scale-110" strokeWidth={1.8} />
                <h3 className="mt-3 text-sm font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="pour-qui" className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Pour qui</div>
          <h2 className="mt-3 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl">Pensé pour les entreprises qui gèrent de nombreuses demandes clients.</h2>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {SECTORS.map((s) => (
              <div key={s.label} className="group flex items-center gap-3 rounded-sm border border-border bg-background p-4 transition-transform transition-shadow duration-300 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg">
                <s.icon className="h-5 w-5 shrink-0 text-primary transition-transform duration-300 ease-out group-hover:scale-110" strokeWidth={1.8} />
                <span className="text-sm font-medium">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border" data-testid="section-en-action">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Le produit</div>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Découvrez Suivio360 en action.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Un aperçu concret de l'interface utilisée par vos équipes et vos clients au quotidien — présenté ici avec des données de démonstration fictives.
            </p>
          </div>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            {[
              { testid: "mockup-dashboard", title: "Tableau de bord", caption: "Pilotez l'activité en un coup d'œil : volumes, statuts et priorités.", wide: true },
              { testid: "mockup-tickets", title: "Liste des tickets", caption: "Chaque demande est classée, filtrable et suivie : plus rien ne se perd." },
              { testid: "mockup-ticket-detail", title: "Détail d'un ticket", caption: "Fil de discussion et notes internes réunis au même endroit." },
              { testid: "mockup-portal", title: "Portail client", caption: "Vos clients créent et suivent leurs demandes en toute autonomie." },
              { testid: "mockup-emails", title: "Notifications email", caption: "Vos équipes et vos clients sont alertés à chaque étape." },
            ].map((m, i) => (
              <motion.div key={m.testid} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 2) * 0.08 }}
                className={`min-w-0 ${m.wide ? "md:col-span-2" : ""}`}>
                {m.testid === "mockup-dashboard" ? (
                  <DashboardMockup testid={m.testid} />
                ) : (
                  <MockupFrame title={`Suivio360 — ${m.title}`} testid={m.testid}>
                    {m.testid === "mockup-tickets" && <TicketsListMockup />}
                    {m.testid === "mockup-ticket-detail" && <TicketDetailMockup />}
                    {m.testid === "mockup-portal" && <PortalMockup />}
                    {m.testid === "mockup-emails" && <EmailLogMockup />}
                  </MockupFrame>
                )}
                <h3 className="mt-4 text-sm font-semibold">{m.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{m.caption}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 sm:py-28 lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Fonctionnement</div>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Comment ça fonctionne ?</h2>
            <div className="mt-6 space-y-0">
              {STEPS.map((s, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-primary font-heading text-sm font-semibold text-white">{i + 1}</div>
                    {i < STEPS.length - 1 && <div className="w-px flex-1 bg-border" />}
                  </div>
                  <p className="pb-8 pt-1.5 text-sm leading-relaxed">{s}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Sécurité & traçabilité</div>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Une vision claire, sécurisée et traçable.</h2>
            <div className="mt-6 space-y-3">
              {SECURITY.map((s) => (
                <div key={s} className="flex items-center gap-3 rounded-sm border border-border bg-card p-4">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />
                  <span className="text-sm">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-neutral-950 text-neutral-200" data-testid="section-rgpd">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Sécurité & conformité</div>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Vos données hébergées en Europe, dans un cadre pensé pour le RGPD.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-400">
              Suivio360 est conçu pour centraliser vos demandes clients dans un environnement sécurisé, avec un hébergement européen sur infrastructure IONOS.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              { icon: Server, title: "Hébergement européen", testid: "rgpd-card-hebergement",
                text: "Suivio360 est destiné à être hébergé sur une infrastructure IONOS en France ou en Europe, afin de limiter les transferts hors Union européenne et de renforcer la maîtrise des données professionnelles." },
              { icon: ShieldCheck, title: "Pensé pour le RGPD", testid: "rgpd-card-rgpd",
                text: "Suivio360 aide les entreprises à mieux organiser leurs données clients grâce aux accès par rôles, à l'historique des actions, aux logs d'activité et à la centralisation des échanges liés aux tickets." },
              { icon: Globe, title: "Souveraineté des données", testid: "rgpd-card-souverainete",
                text: "En s'appuyant sur un hébergeur européen, Suivio360 limite la dépendance aux grands clouds extra-européens et contribue à réduire l'exposition aux transferts de données hors Union européenne." },
            ].map((c, i) => (
              <motion.div key={c.testid} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-sm border border-neutral-800 bg-white/5 p-6 transition-transform transition-shadow duration-300 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl" data-testid={c.testid}>
                <c.icon className="h-5 w-5 text-primary" strokeWidth={1.8} />
                <h3 className="mt-3 font-semibold text-white">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{c.text}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 rounded-sm border border-neutral-800 bg-white/5 p-6" data-testid="rgpd-reassurance">
            <p className="max-w-3xl text-sm leading-relaxed text-neutral-300">
              Accès sécurisés, rôles utilisateurs, historique des tickets, logs d'activité et centralisation des échanges : Suivio360 aide vos équipes à garder le contrôle sur les données clients.
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="rgpd-badges">
              {["Données hébergées en Europe", "Accès par rôles", "Historique complet", "Logs d'activité"].map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 rounded-sm border border-neutral-700 px-2.5 py-1.5 text-xs font-medium text-neutral-300">
                  <Check className="h-3.5 w-3.5 text-primary" /> {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="offres" className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:py-28">
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Offres</div>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Une offre adaptée à votre taille.</h2>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3 md:items-stretch">
            {PLANS.map((p) => (
              <div key={p.name} data-testid={`plan-${slug(p.name)}`}
                className={`relative flex flex-col rounded-md border p-7 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${p.highlight ? "border-primary bg-background shadow-md ring-1 ring-primary" : "border-border bg-background"}`}>
                {p.highlight && <div className="absolute -top-3 left-6 rounded-sm bg-primary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">Recommandé</div>}
                <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-heading text-3xl font-semibold tracking-tight">{p.price}</span>
                  {p.suffix && <span className="text-xs text-muted-foreground">{p.suffix}</span>}
                </div>
                <div className={`mt-5 border-t pt-5 ${p.highlight ? "border-primary/20" : "border-border"}`}>
                  <ul className="space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto pt-6">
                  <button data-testid={`plan-demo-${slug(p.name)}`} onClick={() => scrollTo("demo")}
                    className={`w-full rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors ${p.highlight ? "bg-primary text-white hover:bg-red-700" : "border border-border hover:bg-accent"}`}>
                    Demander une démo
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="demo" className="border-b border-border">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 sm:py-28 lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Démo</div>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Prêt à reprendre le contrôle sur vos demandes clients ?</h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              Demandez une démonstration personnalisée et découvrez comment Suivio360 peut s'adapter à votre organisation.
            </p>
            <div className="mt-6 space-y-2.5 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0 text-primary" /> Réponse sous 24 h ouvrées.</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-primary" /> Aucune carte bancaire requise.</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 shrink-0 text-primary" /> Démonstration adaptée à votre secteur.</div>
            </div>
          </div>
          <div className="rounded-md border border-border bg-card p-6 shadow-sm sm:p-8">
            <DemoForm />
          </div>
        </div>
      </section>

      <footer className="bg-neutral-950 text-neutral-300">
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="flex flex-wrap items-start justify-between gap-8">
            <div className="max-w-xs">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-primary font-heading text-sm font-bold text-white">S</div>
                <span className="font-heading font-semibold text-white">Suivio<span className="text-primary">360</span></span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">Centralisez, suivez et traitez toutes vos demandes clients.</p>
            </div>
            <div className="flex gap-14">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Produit</div>
                <div className="mt-3 space-y-2">
                  <button onClick={() => scrollTo("fonctionnalites")} className="block text-sm text-neutral-300 transition-colors hover:text-white">Fonctionnalités</button>
                  <button onClick={() => scrollTo("offres")} className="block text-sm text-neutral-300 transition-colors hover:text-white">Offres</button>
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Accès</div>
                <div className="mt-3 space-y-2">
                  <Link to="/login" data-testid="footer-login-link" className="block text-sm font-medium text-white transition-colors hover:text-primary">Se connecter</Link>
                  <button data-testid="footer-demo-link" onClick={() => scrollTo("demo")} className="block text-sm text-neutral-300 transition-colors hover:text-white">Demander une démo</button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-10 border-t border-neutral-800 pt-6">
            <p className="text-xs text-neutral-500">© 2026 Suivio360. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
